import { GameOverview } from 'services/matchday';

type CrestLike =
	| string
	| {
			url?: string;
			altText?: string;
			imageVariants?: Array<{ alias?: string; url?: string }> | null;
	  }
	| undefined;

const resolveCrestUrl = (crest: CrestLike): string | undefined => {
	if (!crest) return undefined;
	if (typeof crest === 'string') return crest;
	return crest.url;
};

const resolveCrestWithBackground = (crest: CrestLike): string | undefined => {
	if (!crest || typeof crest === 'string') return undefined;
	return crest.imageVariants?.find(
		(variant) => variant.alias === 'crestWithBackground'
	)?.url;
};

const formatKickOffLabel = (kickOffUTC?: string) => {
	if (!kickOffUTC) return undefined;
	return new Date(kickOffUTC).toLocaleTimeString(undefined, {
		hour: '2-digit',
		minute: '2-digit',
	});
};

const formatCompetitionStatus = (
	overview: GameOverview,
	kickOffTimeZone?: string
) => {
	if (overview.isHalfTime) {
		return overview.matchTime || 'HT';
	}

	if (overview.isLive) {
		if (overview.matchTime) return overview.matchTime;
		if (typeof overview.matchMinute === 'number') {
			return `${overview.matchMinute}'`;
		}
		return 'LIVE';
	}

	if (overview.isPostMatch) {
		return overview.matchTime || 'FT';
	}

	const kickOff = formatKickOffLabel(overview.kickOffUTC);
	if (!kickOff) return undefined;
	return kickOffTimeZone ? `${kickOff} ${kickOffTimeZone}` : kickOff;
};

const resolveTeamLabel = (team?: string) => {
	switch (team?.toLowerCase()) {
		case 'mens':
		case "men's":
			return "Men's";
		case 'womens':
		case "women's":
			return "Women's";
		case 'eds':
			return 'EDS';
		default:
			return undefined;
	}
};

const resolveCtaLabel = (overview: GameOverview) => {
	if (overview.isLive || overview.isHalfTime) return 'Follow live';
	if (overview.isPostMatch) return 'Full-time analysis';
	return 'Follow live';
};

const resolveState = (overview: GameOverview) => {
	if (overview.isHalfTime) return 'half-time' as const;
	if (overview.isLive) return 'live' as const;
	if (overview.isPostMatch) return 'full-time' as const;
	return 'pre-match' as const;
};

const resolveHeroImage = (overview: GameOverview) => {
	const landscape = overview.headerPlaceholder?.landscapeImage;
	const portrait = overview.headerPlaceholder?.portraitImage || landscape;
	const fallbackAlt = `${overview.homeTeamName || 'Home'} vs ${overview.awayTeamName || 'Away'}`;

	return {
		url: portrait?.url || landscape?.url || '',
		alt:
			portrait?.alt ||
			portrait?.altText ||
			landscape?.alt ||
			landscape?.altText ||
			fallbackAlt,
		landscapeUrl: landscape?.url,
		landscapeAlt: landscape?.alt || landscape?.altText || fallbackAlt,
		doNotCrop: overview.headerPlaceholder?.doNotCrop,
	};
};

export const mapGameOverviewToCardMdc = (
	overview: GameOverview,
	options?: {
		pageUrl?: string;
		teamLabel?: string;
		kickOffTimeZone?: string;
	}
) => {
	const state = resolveState(overview);
	const showScores =
		state === 'live' || state === 'half-time' || state === 'full-time';
	const homeCrest = overview.homeTeamCrest;
	const awayCrest = overview.awayTeamCrest;
	const competitionCrest =
		overview.competition?.crest || overview.competition?.colouredCrest;

	return {
		state,
		teamLabel: resolveTeamLabel(options?.teamLabel),
		competitionName: overview.competition?.name,
		competitionCrestUrl: resolveCrestUrl(competitionCrest),
		competitionBackgroundUrl: overview.statusBarBackground?.url,
		competitionStatus: formatCompetitionStatus(
			overview,
			options?.kickOffTimeZone
		),
		competitionStatusAccent: state === 'live' || state === 'half-time',
		homeTeam: {
			name: overview.homeTeamName || 'Home',
			crestUrl:
				resolveCrestWithBackground(homeCrest) ||
				resolveCrestUrl(homeCrest),
			crestAlt:
				(typeof homeCrest === 'object'
					? homeCrest?.altText
					: undefined) || overview.homeTeamName,
		},
		awayTeam: {
			name: overview.awayTeamName || 'Away',
			crestUrl:
				resolveCrestWithBackground(awayCrest) ||
				resolveCrestUrl(awayCrest),
			crestAlt:
				(typeof awayCrest === 'object'
					? awayCrest?.altText
					: undefined) || overview.awayTeamName,
		},
		homeScore: showScores ? (overview.homeTeamScore ?? 0) : undefined,
		awayScore: showScores ? (overview.awayTeamScore ?? 0) : undefined,
		kickOffLabel: formatKickOffLabel(overview.kickOffUTC),
		matchMinuteLabel:
			overview.matchTime ||
			(typeof overview.matchMinute === 'number'
				? `${overview.matchMinute}'`
				: undefined),
		halfTimeLabel: overview.matchTime || 'HT',
		fullTimeLabel: overview.matchTime || 'FT',
		image: resolveHeroImage(overview),
		video: overview.videoId ? { videoId: overview.videoId } : undefined,
		ctaHref: options?.pageUrl || overview.pageUrl || '#',
		ctaLabel: resolveCtaLabel(overview),
		showStatusIndicator: state === 'live' || state === 'half-time',
		kickOffUTC: overview.kickOffUTC,
	};
};

export const resolveMatchBackgroundUrl = (overview?: GameOverview) => {
	if (!overview) return undefined;
	return (
		overview.headerBackground?.landscapeImage?.url ||
		overview.headerPlaceholder?.landscapeImage?.url ||
		overview.headerBackground?.portraitImage?.url ||
		overview.headerPlaceholder?.portraitImage?.url
	);
};
